import React, { useState, useRef, useEffect, useContext } from 'react';
import * as dfd from "danfojs";
import { Tabs, Tab, Box, Button } from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { Scatter } from 'react-chartjs-2';
import { MapContext } from '../MapContext';
import { CatalogContext } from '../CatalogContext';
import AddGeneButton from '../Gene/AddGeneButton';


import {
  Chart as ChartJS,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';

import './FactorViewDrawer.css';

ChartJS.register(
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend);


const FactorViewer = ({ df }) => {

  const [currentTab, setCurrentTab] = useState(0);
  const [dfsub, setDfsub] = useState(null);
  const [factors, setFactors] = useState([]);
  const [factorNumber, setFactorNumber] = useState(0);
  const fileInput = useRef();

  useEffect(() => {
    if (df) {
      setFactors(df["factor"].unique().sortValues().values.map((f) => { return { id: f, alias: '' } }));
      const newDfsub = df.iloc({ rows: df["factor"].eq(factorNumber) });
      setDfsub(newDfsub);
    }
  }, [df])

  useEffect(() => {
    if (dfsub) {
      const newDfsub = df.iloc({ rows: df["factor"].eq(factorNumber) });
      setDfsub(newDfsub);
    };
  }, [factorNumber])


  const handleFileChosen = (event) => {
    let file = event.target.files[0];
    dfd.readCSV(file).then(cdf => {
      const data = dfd.toJSON(cdf, { format: 'column' });
      setFactors(data);
    });
  }
 
  return (
    <>
      <Box className="single-factor-view">
        <div className="factor-top">
        </div>
        <div className="factorList">
          <div className="aliasButton">
            <Button onClick={() => { fileInput.current.click() }}>Load Alias</Button>
            <input
              type='file'
              ref={fileInput}
              style={{ display: 'none' }}
              onClick={(event) => { event.target.value = null }}
              onChange={handleFileChosen}
            />
          </div>
          <FactorTable
            factors={factors}
            setFactorNumber={setFactorNumber}  
          />
        </div>
        <div className="factorNumber">
          <h4>{ `Current Factor #${factorNumber}`}</h4>
        </div>
        <div className='tabContainer'>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Top Genes" />
            <Tab label="Volcano Plot" />
          </Tabs>
          {currentTab === 0 && ( 
            <TopGeneGrid dfsub={dfsub} classname='topGeneGrid'/>
          )}
          {currentTab === 1 && ( 
            <div className='volcanoPlot'>
                <VolcanoPlot dfsub={dfsub} /> 
            </div>
          )}
        </div>
      </Box>
    </>
  );
}

export default FactorViewer;


const FactorTable = React.memo(({ factors, setFactorNumber }) => {
  
  const columns = [
    { field: 'color', headerName: 'color', flex: .1 },
    { field: 'id', headerName: 'id', flex: 0.3 },
    { field: 'alias', headerName: 'alias', flex: 2, editable: true }];
  
  const handleRowClick = (e) => {
    setFactorNumber(e.row.id);
  }

  return <DataGrid
    initialState={{
      pagination: { paginationModel: { pageSize: 12 } },
    }}
    pageSizeOptions={[12, 24, 36]}
    disableRowSelectionOnClick
    components={{ Toolbar: CustomToolbar, }}
    onRowClick={handleRowClick}
    rows={factors}
    columns={columns}
    getRowId={(row) => row.id}
    pageSize={12}
    rowHeight={25}
    hideFooter={false}
    autoHeight={true}
    density='compact'
    sx={{ color: '#AAA' }}
  />
});

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

const TopGeneGrid = ({ dfsub }) => {
  
  const { map }  = useContext(MapContext);
  const { catalog } = useContext(CatalogContext);
  
  const renderViewButton = (params) => {
    const genePMTilesLocation = catalog?.assets.sge.perGene;
    return (
      genePMTilesLocation ?
      <AddGeneButton
        row={params.row}
        map={map}
        gene_field='gene'
        genePMTilesLocation={genePMTilesLocation}
      />
      : null
    )
  }

  const columns = [
    {
      field: 'gene', headerName: 'gene', flex: 1.5,
      renderCell: renderViewButton, 
      disableClickEventBubbling: true,
    },
    {
      field: 'FoldChange', headerName: 'FoldChange', flex: 1,
      type: 'number', disableClickEventBubbling: true,
      valueFormatter: ({ value }) => {
        if (value === Infinity) {
          return '∞'; // or "Infinity", or any other representation you prefer
        } else if (value === -Infinity) {
          return '-∞'; // or "-Infinity", or any other representation you prefer
        } else if (isNaN(value)) {
          return 'NaN'; // Handle NaN values as well, if necessary
        } else {
          return value.toFixed(2);
        }
      },
    },
    {
      field: 'Chi2', headerName: 'Chi2', flex: 1,
      type: 'number', disableClickEventBubbling: true,
      hide: false
    },
    {
      field: 'pval', headerName: 'pval', flex: 1,
      type: 'number', disableClickEventBubbling: true,
      hide: false
    },
    {
      field: 'gene_total', headerName: 'gene_total', flex: 1,
      type: 'number', disableClickEventBubbling: true,
      hide: false
    }
  ];

  const data = dfsub ? dfd.toJSON(dfsub, { format: 'column' }) : null;

  return <>
    { data && (<DataGrid
      initialState={{
        pagination: { paginationModel: { pageSize: 20 } },
      }}
      pageSizeOptions={[10, 20, 30]}
      disableRowSelectionOnClick
      rows={data}
      columns={columns}
      getRowId={(row) => row.gene}
      pageSize={20}
      rowHeight={25}
      hideFooter={false}
      autoHeight={true}
      density='compact'
      sx={{ color: '#AAA' }}
    />)}
  </>
}

const VolcanoPlot = ({ dfsub }) => {

  const options = {
    layout: { padding: { bottom: 0 } },
    // maintainAspectRatio: false,
    aspectRatio: 1,
    responsive: true,
    elements: {
      point: {
        backgroundColor: "#f26bb5D3",
        hoverRadius: 8
      }
    },
    parsing: {
      xAxisKey: 'FoldChange',
      yAxisKey: '-log(pval)',
    },
    scales: {
      x: {
        type: 'linear',
        display: false,
        grid: { display: false },
      },
      x: {
        title: {
          text: "Fold Change",
          display: true
        },
        type: 'logarithmic',
        position: 'bottom',
        grid: {
          color: "#60606090"
        }

      },
      y: {
        type: 'linear',
        reverse: true,
        display: false,
        grid: { display: false }
      },
      y: {
        title: {
          text: `-log10(P-Value)`,
          display: true
        },
        type: 'linear',
        position: 'left',
        grid: {
          color: "#60606090"
        }
      },
    },
    interaction: { mode: 'nearest' },
    plugins: {
      title: {
        display: true,
        text: 'Volcano Plot',
      },
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (tooltipItem) => { return tooltipItem[0].raw.gene },
        }
      }
    }
  };
  const data = {
    datasets: [
      { data: dfsub ? dfd.toJSON(dfsub, { format: 'column' }) : null }
    ]
  };
  return <Scatter data={data} options={options} />;
}
